<template>
  <FadeTransition>
    <!-- FIXME: dolphin jump用に2つのコンポーネントをv-ifで囲うためにtemplateを使っている。
      dolphin jumpが不要になったら消すこと -->
    <template v-if="planner && corporation">
      <PlannerEditorTable
        :planner="planner"
        :viewer="props.viewer"
        :corporation="corporation"
        :branches="corporation.branches"
        :selectableSpecialties="selectableSpecialties"
        :isEditing="isEditing"
        :isButtonDisabled="isSending"
        @edit="isEditing = true"
        @save="handleSave"
        @cancel="isEditing = false"
      />
    </template>
    <div v-else :class="$style.loader">
      <LoadingIndicator />
    </div>
  </FadeTransition>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import type { FormValue } from './PlannerEditorTable.vue'
import PlannerEditorTable from './PlannerEditorTable.vue'
import FadeTransition from '@/components/transitions/FadeTransition.vue'
import LoadingIndicator from '@/components/atoms/LoadingIndicator.vue'
import type { Planner, Corporation, Specialty } from '@/types/app'
import { useHead } from '@unhead/vue'
import { onBeforeRouteLeave, useRouter } from 'vue-router'
import { useAlert } from '@/libs/alertPortal'
import {
  type SettingsPagePlannerViewViewerFragment,
  useSettingsPagePlannerCorporationViewQuery,
} from './SettingsPagePlannerView.generated'
import {
  useIssuePlannerCoverImageUploadUrlMutation,
  useUpdatePlannerProfileMutation,
  useSettingsPagePlannerViewQuery,
  useUpdatePlannerMutation,
} from './SettingsPagePlannerView.generated'
import { isClientError } from '@/libs/utilities/CustomErrors'
import { captureException } from '@sentry/vue'
import axios from 'axios'
import { getAPIBaseURL } from '@/config'
import { generateFlexImage } from '@/libs/utilities/FlexImage'
import { parseCorporationResponseToCorporation } from '@/apis/repositories/parsers/corporation.ts'
import { parsePlannerProfileResponseToPlanner } from '@/apis/repositories/parsers/planner.ts'
import { useMaybeRoute } from '@/features/router/route'

const props = defineProps<{
  viewer: SettingsPagePlannerViewViewerFragment
}>()

const emit = defineEmits<{
  (
    event: 'onBeforeLeaveConfirmation',
    callbacks: { onConfirm: () => void; onAbort: () => void }
  ): void
}>()

const maybeEditingRoute = useMaybeRoute('plannerSettingsEditing')

const router = useRouter()

const isSending = ref(false)
const isCompleted = ref(false)

const isEditing = computed({
  get: (): boolean => {
    return maybeEditingRoute.value != undefined
  },
  set: (value: boolean) => {
    const name = value ? 'plannerSettingsEditing' : 'plannerSettings'
    // eslint-disable-next-line fp/no-mutating-methods
    void router.push({ name })
  },
})

const pageQuery = useSettingsPagePlannerViewQuery()

const planner = computed<Planner | undefined>(() => {
  const data = pageQuery.result?.value?.planner
  const allSpecialty = pageQuery.result?.value?.specialties || []
  if (data) {
    return parsePlannerProfileResponseToPlanner(
      {
        ...data,
        id: parseInt(data.id),
        corporationId: parseInt(data.corporationId),
        branchIds: data.branchIds.map(id => parseInt(id)),
        specialties: data.specialties.map(specialty => specialty.name),
      },
      allSpecialty.map(specialty => ({
        ...specialty,
        id: parseInt(specialty.id),
      }))
    )
  }
  return undefined
})

const selectableSpecialties = computed<Specialty[]>(() => {
  return (
    pageQuery.result?.value?.specialties.map(specialty => ({
      ...specialty,
      id: parseInt(specialty.id),
    })) || []
  )
})

const pageCorporationQuery = useSettingsPagePlannerCorporationViewQuery(
  () => ({
    id: planner.value ? planner.value.corporationId.toString() : '',
  }),
  () => ({
    enabled: !!planner.value,
  })
)

const corporation = computed<Corporation | null>(() => {
  const data = pageCorporationQuery.result?.value?.corporation
  if (data) {
    return parseCorporationResponseToCorporation({
      ...data,
      id: parseInt(data.id),
      branches: data.branches.map(branch => ({
        ...branch,
        id: parseInt(branch.id),
      })),
    })
  }
  return null
})

onBeforeRouteLeave((to, from, next) => {
  if (from.name !== 'plannerSettingsEditing') {
    next()
    return
  }
  if (to.name === 'plannerSettings' && isCompleted.value) {
    isCompleted.value = false
    next()
    return
  }
  emit('onBeforeLeaveConfirmation', {
    onConfirm: () => {
      isCompleted.value = true
      next()
    },
    onAbort: () => next(false),
  })
})

useHead({
  title: () =>
    isEditing.value ? '編集 | プロフィール設定' : 'プロフィール設定',
})

const alert = useAlert()

const { mutate: updatePlannerProfileMutation } =
  useUpdatePlannerProfileMutation({})

const { mutate: updatePlannerMutation } = useUpdatePlannerMutation({})

const { mutate: issuePlannerCoverImageUploadUrl } =
  useIssuePlannerCoverImageUploadUrlMutation({})

const allowedImageMimeType = ['image/jpeg', 'image/png', 'image/gif']

const imgixDomain = new URL(getAPIBaseURL('imgix')).host

const onlineMatchingStatusID = '1'

const uploadImageIfNeeded = async (planner: Planner, file: Maybe<File>) => {
  if (!file) {
    return planner.coverImage
  }

  if (!allowedImageMimeType.includes(file.type)) {
    alert.error('画像ファイルの形式が不正です。')
    return
  }

  const res = await issuePlannerCoverImageUploadUrl({
    size: file.size,
    contentType: file.type,
  })

  const data = res?.data?.issuePlannerCoverImageUploadUrl
  if (!data) {
    alert.error('画像のアップロードに失敗しました。')
    return
  }

  await axios.put(data.uploadURL, file)

  return generateFlexImage(imgixDomain, data.path)
}

const handleSave = async (value: FormValue): Promise<void> => {
  isSending.value = true
  try {
    const coverImage = await uploadImageIfNeeded(
      value.planner,
      value.coverImageFile
    )

    await updatePlannerProfileMutation({
      input: {
        childrenState: value.childrenState,
        address: value.address,
      },
    })

    await updatePlannerMutation({
      input: {
        gender: value.planner.sex,
        birthday: value.planner.birthday.toISOString() as DateString,
        availableTime: value.planner.availableTimeText,
        comment: value.planner.selfIntroductionText,
        coverFileName: coverImage?.filename,
        avatarFileName: value.planner.avatarImage?.filename,
        availableAreaCodes: value.planner.availablePrefectureCodes,
        specialties: value.planner.specialties.map(specialty =>
          specialty.id.toString()
        ),
      },
    })

    isCompleted.value = true
    isEditing.value = false
    alert.success('入力した内容が変更されました。')
  } catch (error) {
    alert.error('更新処理中にエラーが発生しました。')

    if (!isClientError(error)) {
      captureException(error)
    }
  } finally {
    isSending.value = false
  }
}
</script>

<style lang="scss" module>
.loader {
  display: flex;
  height: 560px;
  align-items: center;
  justify-content: center;
}
</style>
